/**
 * LocationWorkspace
 *
 * Replaces the map area on the Command Center welcome screen when the user
 * is focused on a specific location (detected via geolocation or manually selected).
 * Shows a summary card with location info, health score, stats, asset type
 * breakdown, and quick actions.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import {
	EnvironmentOutlined,
	ScanOutlined,
	CameraOutlined,
	UnorderedListOutlined,
	FormOutlined,
	EyeOutlined,
	SwapOutlined,
	HeartOutlined,
	AppstoreOutlined,
	SortAscendingOutlined,
	SortDescendingOutlined,
	ToolOutlined,
	PlusOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import { Button, Tooltip, Spin, Modal, Tag, Badge } from "antd";
import type { LocationAggregate } from "@/models/organization/dashboard-analytics/dashboard-analytics.model";
import { getAssetTypeSummary, getAssetsByType } from "@/services/asset-service";
import {
	getWorkOrdersByLocation,
	getWorkOrderStats,
	type WorkOrder,
	type WorkOrderStats,
} from "@/services/work-order-service";
import {
	getRecordsByLocation,
	getCollectionStats,
	getTemplates,
	type CollectionRecord,
	type CollectionStats,
	type CollectionTemplate,
} from "@/services/data-collection-service";
import { listInventoryStock } from "@/services/inventory-service";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { WorkOrderQuickCreate } from "./WorkOrderQuickCreate";
import { WorkOrderDetail } from "./WorkOrderDetail";
import { CollectionCapture } from "./CollectionCapture";
import { CollectionDetail } from "./CollectionDetail";
import "./LocationWorkspace.scss";

interface AssetTypeSummaryItem {
	typeName: string;
	count: number;
	healthScore: number;
}

interface LocationWorkspaceProps {
	location: LocationAggregate;
	onExit: () => void;
	onScanAsset: () => void;
	onNavigate: (path: string) => void;
	onCollectAsset?: () => void;
}

const LocationWorkspace = ({
	location,
	onExit,
	onScanAsset,
	onNavigate,
	onCollectAsset,
}: LocationWorkspaceProps) => {
	const [assetTypeSummary, setAssetTypeSummary] = useState<AssetTypeSummaryItem[]>([]);
	const [summaryLoading, setSummaryLoading] = useState(true);
	const [sortBy, setSortBy] = useState<"count" | "name">("count");

	// Modal state
	const [modalOpen, setModalOpen] = useState(false);
	const [modalTypeName, setModalTypeName] = useState("");
	const [modalAssets, setModalAssets] = useState<any[]>([]);
	const [modalLoading, setModalLoading] = useState(false);

	// ─── Work Order state ───
	const hasWorkOrders = useFeatureFlag("work_orders");
	const [woStats, setWoStats] = useState<WorkOrderStats | null>(null);
	const [recentWOs, setRecentWOs] = useState<WorkOrder[]>([]);
	const [woLoading, setWoLoading] = useState(false);
	const [showWOCreate, setShowWOCreate] = useState(false);
	const [selectedWO, setSelectedWO] = useState<WorkOrder | null>(null);

	// ─── Inventory state ───
	const hasInventory = useFeatureFlag("inventory");
	const [inventoryStats, setInventoryStats] = useState<{ totalStockRows: number; lowStockRows: number } | null>(null);

	// ─── Data Collection state ───
	const hasDataCollection = useFeatureFlag("data_collection");
	const [dcStats, setDcStats] = useState<CollectionStats | null>(null);
	const [recentCollections, setRecentCollections] = useState<CollectionRecord[]>([]);
	const [dcTemplates, setDcTemplates] = useState<CollectionTemplate[]>([]);
	const [dcLoading, setDcLoading] = useState(false);
	const [showCollectionCapture, setShowCollectionCapture] = useState(false);
	const [selectedCollection, setSelectedCollection] = useState<CollectionRecord | null>(null);

	const healthScore = location.health?.healthScore ?? 0;
	const locationName = location.name || location.address1 || "Location";
	const addressLine = [location.city, location.state].filter(Boolean).join(", ");
	const fullAddress = location.address1
		? `${location.address1}${addressLine ? `, ${addressLine}` : ""}${location.zip ? ` ${location.zip}` : ""}`
		: addressLine;
	const totalAssets = (location as any).submissionCount ?? (location as any).submission_count ?? 0;

	// Fetch asset type breakdown when location changes
	useEffect(() => {
		let cancelled = false;
		setSummaryLoading(true);

		getAssetTypeSummary(location.id)
			.then((data) => {
				if (!cancelled) setAssetTypeSummary(data);
			})
			.catch(() => {
				if (!cancelled) setAssetTypeSummary([]);
			})
			.finally(() => {
				if (!cancelled) setSummaryLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [location.id]);

	// Fetch work order data when feature is enabled
	useEffect(() => {
		if (!hasWorkOrders) return;
		let cancelled = false;
		setWoLoading(true);

		Promise.all([
			getWorkOrderStats(location.id).catch(() => null),
			getWorkOrdersByLocation(location.id).catch(() => ({ workOrders: [] })),
		]).then(([stats, wos]) => {
			if (cancelled) return;
			setWoStats(stats);
			setRecentWOs((wos?.workOrders ?? []).slice(0, 5));
			setWoLoading(false);
		});

		return () => { cancelled = true; };
	}, [location.id, hasWorkOrders]);

	// Fetch inventory stats for this location when feature is enabled
	useEffect(() => {
		if (!hasInventory) return;
		let cancelled = false;
		listInventoryStock({ locationId: location.id, limit: 200 })
			.then((res) => {
				if (cancelled) return;
				const lowStockRows = res.stock.filter((s) => s.isLowStock).length;
				setInventoryStats({ totalStockRows: res.stock.length, lowStockRows });
			})
			.catch(() => {
				if (!cancelled) setInventoryStats(null);
			});
		return () => { cancelled = true; };
	}, [location.id, hasInventory]);

	// Fetch data collection when feature is enabled
	useEffect(() => {
		if (!hasDataCollection) return;
		let cancelled = false;
		setDcLoading(true);

		Promise.all([
			getCollectionStats(location.id).catch(() => null),
			getRecordsByLocation(location.id, 5).catch(() => []),
			getTemplates().catch(() => []),
		]).then(([stats, records, templates]) => {
			if (cancelled) return;
			setDcStats(stats);
			setRecentCollections(records);
			setDcTemplates(templates);
			setDcLoading(false);
		});

		return () => { cancelled = true; };
	}, [location.id, hasDataCollection]);

	const refreshCollections = useCallback(async () => {
		if (!hasDataCollection) return;
		try {
			const [stats, records] = await Promise.all([
				getCollectionStats(location.id),
				getRecordsByLocation(location.id, 5),
			]);
			setDcStats(stats);
			setRecentCollections(records);
		} catch { /* ignore */ }
	}, [location.id, hasDataCollection]);

	const refreshWorkOrders = useCallback(async () => {
		if (!hasWorkOrders) return;
		try {
			const [stats, wos] = await Promise.all([
				getWorkOrderStats(location.id),
				getWorkOrdersByLocation(location.id),
			]);
			setWoStats(stats);
			setRecentWOs((wos?.workOrders ?? []).slice(0, 5));
		} catch { /* ignore */ }
	}, [location.id, hasWorkOrders]);

	// Sorted summary list
	const sortedSummary = useMemo(() => {
		const sorted = [...assetTypeSummary];
		if (sortBy === "name") {
			sorted.sort((a, b) => a.typeName.localeCompare(b.typeName));
		} else {
			sorted.sort((a, b) => b.count - a.count || a.typeName.localeCompare(b.typeName));
		}
		return sorted;
	}, [assetTypeSummary, sortBy]);

	const toggleSort = useCallback(() => {
		setSortBy((prev) => (prev === "count" ? "name" : "count"));
	}, []);

	// Open modal and fetch assets for the selected type
	const handleTypeClick = useCallback(
		async (typeName: string) => {
			setModalTypeName(typeName);
			setModalOpen(true);
			setModalLoading(true);
			setModalAssets([]);

			try {
				const assets = await getAssetsByType(location.id, typeName);
				setModalAssets(assets);
			} catch {
				setModalAssets([]);
			} finally {
				setModalLoading(false);
			}
		},
		[location.id],
	);

	const getHealthColor = (score: number) => {
		if (score >= 80) return "good";
		if (score >= 50) return "fair";
		return "poor";
	};

	const getTypeHealthClass = (score: number) => {
		if (!score) return "lw-chip-neutral";
		if (score >= 75) return "lw-chip-good";
		if (score >= 45) return "lw-chip-fair";
		return "lw-chip-poor";
	};

	const quickActions = [
		{
			key: "scan",
			label: "Scan Asset",
			description: "Identify equipment",
			icon: <ScanOutlined />,
			color: "#92278f",
			onClick: onScanAsset,
		},
		{
			key: "assets",
			label: "View Assets",
			description: "Browse equipment",
			icon: <UnorderedListOutlined />,
			color: "#1890ff",
			onClick: () => onNavigate(`/locations/${location.id}`),
		},
		{
			key: "forms",
			label: "Submit Form",
			description: "Capture data",
			icon: <FormOutlined />,
			color: "#52c41a",
			onClick: () => onNavigate("/forms"),
		},
		{
			key: "details",
			label: "View Details",
			description: "Full overview",
			icon: <EyeOutlined />,
			color: "#fa8c16",
			onClick: () => onNavigate(`/locations/${location.id}`),
		},
	];

	return (
		<div className="location-workspace">
			{/* Header */}
			<div className="lw-header">
				<div className="lw-header-info">
					<div className="lw-location-icon">
						<EnvironmentOutlined />
					</div>
					<div className="lw-header-text">
						<h3 className="lw-name">{locationName}</h3>
						{fullAddress && <p className="lw-address">{fullAddress}</p>}
					</div>
				</div>
				<div className="lw-header-actions">
					<Tooltip title="Change or exit location focus">
						<Button
							type="text"
							icon={<SwapOutlined />}
							onClick={onExit}
							className="lw-exit-btn"
						>
							Change
						</Button>
					</Tooltip>
				</div>
			</div>

			{/* Stats row */}
			<div className="lw-stats">
				<div className="lw-stat">
					<HeartOutlined className="lw-stat-icon" />
					<div className="lw-stat-content">
						<span className={`lw-stat-value lw-health-${getHealthColor(healthScore)}`}>
							{healthScore}%
						</span>
						<span className="lw-stat-label">Health Score</span>
					</div>
				</div>
				<div className="lw-stat">
					<AppstoreOutlined className="lw-stat-icon" />
					<div className="lw-stat-content">
						<span className="lw-stat-value">{totalAssets}</span>
						<span className="lw-stat-label">Assets</span>
					</div>
				</div>
				{((location as any).squareFootage > 0 || (location as any).square_footage > 0) && (
					<div className="lw-stat">
						<AppstoreOutlined className="lw-stat-icon" />
						<div className="lw-stat-content">
							<span className="lw-stat-value">
								{Number((location as any).squareFootage || (location as any).square_footage).toLocaleString()}
							</span>
							<span className="lw-stat-label">Sq Ft</span>
						</div>
					</div>
				)}
				{hasWorkOrders && woStats && (
					<div className="lw-stat lw-stat-wo">
						<ToolOutlined className="lw-stat-icon" />
						<div className="lw-stat-content">
							<span className="lw-stat-value">
								{woStats.open + woStats.assigned + woStats.inProgress}
								{woStats.overdue > 0 && (
									<Badge count={woStats.overdue} size="small" style={{ marginLeft: 4, backgroundColor: "#ff4d4f" }} />
								)}
							</span>
							<span className="lw-stat-label">Active WOs</span>
						</div>
					</div>
				)}
				{hasDataCollection && dcStats && (
					<div className="lw-stat lw-stat-dc">
						<FormOutlined className="lw-stat-icon" />
						<div className="lw-stat-content">
							<span className="lw-stat-value">{dcStats.total}</span>
							<span className="lw-stat-label">Collected</span>
						</div>
					</div>
				)}
				{hasInventory && inventoryStats && (
					<button
						type="button"
						className="lw-stat lw-stat-inv"
						onClick={() => onNavigate(`/locations/${location.id}/inventory`)}
						title="Open inventory for this location"
						style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}
					>
						<AppstoreOutlined className="lw-stat-icon" />
						<div className="lw-stat-content">
							<span className="lw-stat-value">
								{inventoryStats.totalStockRows}
								{inventoryStats.lowStockRows > 0 && (
									<Badge
										count={inventoryStats.lowStockRows}
										size="small"
										style={{ marginLeft: 4, backgroundColor: "#fa8c16" }}
									/>
								)}
							</span>
							<span className="lw-stat-label">Inventory</span>
						</div>
					</button>
				)}
			</div>

			{/* Asset Type Breakdown */}
			<div className="lw-type-section">
				<div className="lw-type-header">
					<span className="lw-type-title">Equipment Breakdown</span>
					<button
						type="button"
						className="lw-type-sort-btn"
						onClick={toggleSort}
						title={sortBy === "count" ? "Sort A-Z" : "Sort by count"}
					>
						{sortBy === "count" ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
						<span>{sortBy === "count" ? "Count" : "A-Z"}</span>
					</button>
				</div>
				{summaryLoading ? (
					<div className="lw-type-loading">
						<Spin size="small" />
					</div>
				) : sortedSummary.length > 0 ? (
					<div className="lw-type-grid">
						{sortedSummary.map((item) => (
							<button
								key={item.typeName}
								type="button"
								className={`lw-type-chip ${getTypeHealthClass(item.healthScore)}`}
								onClick={() => handleTypeClick(item.typeName)}
							>
								<span className="lw-type-chip-dot" />
								<span className="lw-type-chip-name">{item.typeName}</span>
								<span className="lw-type-chip-count">{item.count}</span>
							</button>
						))}
					</div>
				) : (
					<div className="lw-type-empty">No asset data</div>
				)}
			</div>

			{/* Work Orders Section */}
			{hasWorkOrders && (
				<div className="lw-wo-section">
					<div className="lw-wo-header">
						<span className="lw-wo-title">
							<ToolOutlined /> Work Orders
						</span>
						<button
							type="button"
							className="lw-wo-create-btn"
							onClick={() => setShowWOCreate(true)}
						>
							<PlusOutlined /> New
						</button>
					</div>
					{woLoading ? (
						<div className="lw-type-loading"><Spin size="small" /></div>
					) : recentWOs.length > 0 ? (
						<div className="lw-wo-list">
							{recentWOs.map((wo) => (
								<button
									key={wo.id}
									type="button"
									className="lw-wo-card"
									onClick={() => setSelectedWO(wo)}
								>
									<div className="lw-wo-card-left">
										<Tag
											color={
												wo.priority === 1 ? "red" :
												wo.priority === 2 ? "orange" :
												wo.priority === 3 ? "gold" :
												"default"
											}
											className="lw-wo-priority-tag"
										>
											P{wo.priority}
										</Tag>
										<div className="lw-wo-card-info">
											<span className="lw-wo-card-title">{wo.title}</span>
											<span className="lw-wo-card-number">{wo.workOrderNumber}</span>
										</div>
									</div>
									<div className="lw-wo-card-right">
										<Tag
											color={
												wo.status === "IN_PROGRESS" ? "processing" :
												wo.status === "COMPLETED" ? "success" :
												wo.status === "ON_HOLD" ? "warning" :
												wo.status === "CANCELLED" ? "error" :
												wo.status === "ASSIGNED" ? "blue" :
												"default"
											}
											className="lw-wo-status-tag"
										>
											{wo.status.replace(/_/g, " ")}
										</Tag>
										{wo.isSlaBreached && (
											<WarningOutlined className="lw-wo-sla-warn" />
										)}
									</div>
								</button>
							))}
						</div>
					) : (
						<div className="lw-wo-empty">No active work orders</div>
					)}
				</div>
			)}

			{/* Data Collection Section */}
			{hasDataCollection && (
				<div className="lw-dc-section">
					<div className="lw-dc-header">
						<span className="lw-dc-title">
							<FormOutlined /> Collected Data
						</span>
						<button
							type="button"
							className="lw-dc-create-btn"
							onClick={() => setShowCollectionCapture(true)}
						>
							<PlusOutlined /> Collect
						</button>
					</div>
					{dcLoading ? (
						<div className="lw-type-loading"><Spin size="small" /></div>
					) : recentCollections.length > 0 ? (
						<div className="lw-dc-list">
							{recentCollections.map((rec) => (
								<button
									key={rec.id}
									type="button"
									className="lw-dc-card"
									onClick={() => setSelectedCollection(rec)}
								>
									<div className="lw-dc-card-left">
										<Tag color="purple" className="lw-dc-category-tag">
											{rec.templateCategory || rec.templateName || "Collection"}
										</Tag>
										<div className="lw-dc-card-info">
											<span className="lw-dc-card-title">
												{rec.templateName || "Data Collection"}
											</span>
											{rec.areaName && (
												<span className="lw-dc-card-area">{rec.areaName}</span>
											)}
										</div>
									</div>
									<div className="lw-dc-card-right">
										<Tag
											color={
												rec.status === "COMPLETED" ? "success" :
												rec.status === "REVIEWED" ? "blue" :
												rec.status === "PROCESSING" ? "processing" :
												rec.status === "FLAGGED" ? "error" :
												"default"
											}
											className="lw-dc-status-tag"
										>
											{rec.status}
										</Tag>
										{rec.healthScoreContribution !== null && rec.healthScoreContribution !== undefined && (
											<span className={`lw-dc-health-badge lw-health-${getHealthColor(rec.healthScoreContribution)}`}>
												{Math.round(rec.healthScoreContribution)}
											</span>
										)}
									</div>
								</button>
							))}
						</div>
					) : (
						<div className="lw-dc-empty">No data collected yet</div>
					)}
				</div>
			)}

			{/* Quick Actions */}
			<div className="lw-actions">
				{quickActions.map((action) => (
					<button
						key={action.key}
						type="button"
						className="lw-action-btn"
						onClick={action.onClick}
					>
						<div
							className="lw-action-icon"
							style={{ background: action.color }}
						>
							{action.icon}
						</div>
						<div className="lw-action-text">
							<span className="lw-action-label">{action.label}</span>
							<span className="lw-action-desc">{action.description}</span>
						</div>
					</button>
				))}
			</div>

			{/* Mobile: Collect Asset CTA (shown inside workspace when location is focused) */}
			{onCollectAsset && (
				<button
					type="button"
					className="lw-mobile-collect-asset"
					onClick={onCollectAsset}
				>
					<div className="lw-mobile-collect-icon">
						<CameraOutlined />
					</div>
					<div className="lw-mobile-collect-text">
						<strong>Collect Asset Here</strong>
						<span>Add a new asset at {location.name || location.address1 || "this location"}</span>
					</div>
				</button>
			)}

			{/* Asset list modal */}
			<Modal
				open={modalOpen}
				onCancel={() => setModalOpen(false)}
				title={null}
				footer={null}
				width={540}
				className="lw-assets-modal"
				destroyOnClose
			>
				<div className="lw-modal-header">
					<h3>{modalTypeName}</h3>
					<span className="lw-modal-count">{modalAssets.length} asset{modalAssets.length !== 1 ? "s" : ""}</span>
				</div>
				{modalLoading ? (
					<div className="lw-modal-loading">
						<Spin />
					</div>
				) : modalAssets.length > 0 ? (
					<div className="lw-modal-list">
						{modalAssets.map((asset: any) => (
							<div key={asset.id} className="lw-modal-asset-row">
								<div className="lw-modal-asset-main">
									{asset.assetTag && (
										<span className="lw-modal-asset-tag">{asset.assetTag}</span>
									)}
									<span className="lw-modal-asset-info">
										{[asset.manufacturer, asset.modelNumber].filter(Boolean).join(" · ") || "—"}
									</span>
								</div>
								<div className="lw-modal-asset-meta">
									{asset.serialNumber && (
										<span className="lw-modal-asset-serial">S/N: {asset.serialNumber}</span>
									)}
									{asset.condition && (
										<span className={`lw-modal-asset-condition lw-condition-${(asset.condition || "").toLowerCase().replace(/\s+/g, "-")}`}>
											{asset.condition}
										</span>
									)}
								</div>
							</div>
						))}
					</div>
				) : (
					<div className="lw-modal-empty">No assets found</div>
				)}
			</Modal>

			{/* WO Quick Create */}
			{hasWorkOrders && (
				<WorkOrderQuickCreate
					open={showWOCreate}
					locationId={location.id}
					onClose={() => setShowWOCreate(false)}
					onCreated={() => {
						setShowWOCreate(false);
						refreshWorkOrders();
					}}
				/>
			)}

			{/* WO Detail */}
			{hasWorkOrders && selectedWO && (
				<WorkOrderDetail
					open={!!selectedWO}
					workOrderId={selectedWO.id}
					onClose={() => setSelectedWO(null)}
					onUpdated={refreshWorkOrders}
				/>
			)}

			{/* Data Collection Capture */}
			{hasDataCollection && (
				<CollectionCapture
					open={showCollectionCapture}
					locationId={location.id}
					templates={dcTemplates}
					onClose={() => setShowCollectionCapture(false)}
					onCreated={() => {
						setShowCollectionCapture(false);
						refreshCollections();
					}}
				/>
			)}

			{/* Data Collection Detail */}
			{hasDataCollection && selectedCollection && (
				<CollectionDetail
					open={!!selectedCollection}
					recordId={selectedCollection.id}
					onClose={() => setSelectedCollection(null)}
					onUpdated={refreshCollections}
				/>
			)}
		</div>
	);
};

export { LocationWorkspace };
